import React, { useState, useEffect } from 'react';
import * as osTemplateService from '../../services/osTemplateService';
import type { OSTemplateFamily, OSTemplate } from '../../services/osTemplateService';

interface OSCategoryCardsProps {
  selectedTemplateId: string | null;
  onSelectTemplate: (templateId: string, template: OSTemplate) => void;
  hostId?: number;
  disabled?: boolean;
  minCpu?: number;
  minRamMb?: number;
  minDiskGb?: number;
}

const OSCategoryCards: React.FC<OSCategoryCardsProps> = ({
  selectedTemplateId,
  onSelectTemplate,
  hostId,
  disabled = false,
  minCpu,
  minRamMb,
  minDiskGb
}) => {
  const [families, setFamilies] = useState<OSTemplateFamily[]>([]);
  const [activeFamily, setActiveFamily] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Family display info
  const familyInfo: Record<string, { icon: string; color: string; bgColor: string }> = {
    ubuntu: { icon: '🟠', color: 'text-orange-600', bgColor: 'bg-orange-50' },
    debian: { icon: '🔴', color: 'text-red-600', bgColor: 'bg-red-50' },
    almalinux: { icon: '🔵', color: 'text-blue-600', bgColor: 'bg-blue-50' },
    rocky: { icon: '🟢', color: 'text-green-600', bgColor: 'bg-green-50' },
    fedora: { icon: '🔷', color: 'text-indigo-600', bgColor: 'bg-indigo-50' },
    centos: { icon: '🟣', color: 'text-purple-600', bgColor: 'bg-purple-50' }
  };

  useEffect(() => {
    fetchTemplates();
  }, [hostId]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError('');

      let data: OSTemplateFamily[];
      if (hostId) {
        data = await osTemplateService.getAvailableTemplatesForHost(hostId);
      } else {
        data = await osTemplateService.getAvailableTemplates();
      }

      setFamilies(data);

      // Set default active family
      if (data.length > 0 && !activeFamily) {
        setActiveFamily(data[0].family);
      }

      // If a template was previously selected, ensure its family is active
      if (selectedTemplateId) {
        for (const family of data) {
          const found = family.templates.find(t => t.id === selectedTemplateId);
          if (found) {
            setActiveFamily(family.family);
            break;
          }
        }
      }
    } catch (err: any) {
      console.error('Error fetching templates:', err);
      setError('Failed to load OS templates');
    } finally {
      setLoading(false);
    }
  };

  // Check if template meets minimum requirements
  const meetsRequirements = (template: OSTemplate): boolean => {
    if (minCpu && template.min_cpu > minCpu) return false;
    if (minRamMb && template.min_ram_mb > minRamMb) return false;
    if (minDiskGb && template.min_disk_gb > minDiskGb) return false;
    return true;
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="flex gap-2">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-10 w-24 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <p>{error}</p>
        <button
          onClick={fetchTemplates}
          className="mt-2 text-sm underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (families.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-700">
        <p>No OS templates are currently available. Please contact support.</p>
      </div>
    );
  }

  const activeTemplates = families.find(f => f.family === activeFamily)?.templates || [];

  return (
    <div className="space-y-4">
      {/* Family Tabs */}
      <div className="flex flex-wrap gap-2">
        {families.map(family => {
          const info = familyInfo[family.family] || { icon: '🐧', color: 'text-gray-600', bgColor: 'bg-gray-50' };
          const isActive = activeFamily === family.family;

          return (
            <button
              key={family.family}
              onClick={() => !disabled && setActiveFamily(family.family)}
              disabled={disabled}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all
                ${isActive
                  ? `${info.bgColor} ${info.color} ring-2 ring-offset-1 ring-current`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <span className="text-lg">{info.icon}</span>
              <span>{family.display_name}</span>
              <span className="text-xs opacity-60">({family.templates.length})</span>
            </button>
          );
        })}
      </div>

      {/* Version Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {activeTemplates.map(template => {
          const isSelected = selectedTemplateId === template.id;
          const isCompatible = meetsRequirements(template);
          const info = familyInfo[template.family] || { icon: '🐧', color: 'text-gray-600', bgColor: 'bg-gray-50' };

          return (
            <div
              key={template.id}
              onClick={() => {
                if (!disabled && isCompatible) {
                  onSelectTemplate(template.id, template);
                }
              }}
              className={`
                relative border-2 rounded-xl p-4 transition-all
                ${isSelected
                  ? 'border-orange-500 bg-orange-50 shadow-lg ring-2 ring-orange-200'
                  : isCompatible
                    ? 'border-gray-200 hover:border-orange-300 hover:shadow-md bg-white'
                    : 'border-gray-200 bg-gray-50 opacity-60'
                }
                ${disabled || !isCompatible ? 'cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {/* Featured Badge */}
              {template.is_featured && (
                <div className="absolute -top-2 -right-2">
                  <span className="px-2 py-1 bg-orange-500 text-white text-xs font-bold rounded-full shadow">
                    Recommended
                  </span>
                </div>
              )}

              {/* Selected Checkmark */}
              {isSelected && (
                <div className="absolute top-2 right-2">
                  <span className="flex items-center justify-center w-6 h-6 bg-orange-500 text-white rounded-full">
                    ✓
                  </span>
                </div>
              )}

              {/* Content */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{info.icon}</span>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {template.version}
                      {template.version.includes('LTS') ? '' : template.code_name?.includes('LTS') ? ' LTS' : ''}
                    </h4>
                    {template.code_name && (
                      <p className="text-xs text-gray-500">{template.code_name}</p>
                    )}
                  </div>
                </div>

                {/* Min Requirements */}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                  <span className="text-xs text-gray-400">
                    Min: {template.min_ram_mb >= 1024 ? `${template.min_ram_mb / 1024}GB` : `${template.min_ram_mb}MB`} RAM
                  </span>
                  <span className="text-xs text-gray-400">
                    • {template.min_disk_gb}GB disk
                  </span>
                </div>

                {/* Incompatible Warning */}
                {!isCompatible && (
                  <div className="mt-2 text-xs text-red-500">
                    Your selected plan doesn't meet minimum requirements
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Selected Template Info */}
      {selectedTemplateId && (
        <div className="bg-gray-50 rounded-lg p-3 text-sm">
          {(() => {
            let selectedTemplate: OSTemplate | undefined;
            for (const family of families) {
              selectedTemplate = family.templates.find(t => t.id === selectedTemplateId);
              if (selectedTemplate) break;
            }
            if (!selectedTemplate) return null;

            return (
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">Selected: </span>
                  <span>{selectedTemplate.display_name}</span>
                  {selectedTemplate.code_name && (
                    <span className="text-gray-500"> ({selectedTemplate.code_name})</span>
                  )}
                </div>
                <div className="text-gray-500 text-xs">
                  Default user: <code className="bg-gray-200 px-1 rounded">{selectedTemplate.default_username}</code>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default OSCategoryCards;
